import { css } from '@emotion/react'
import React, { FC, useRef, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import {
  ButtonPrimary,
  ButtonClean,
  Box,
  Heading,
  FormLabel,
  FormControl,
  Input
} from 'front-end/src/components'
import { Modal } from '../../../../components'
import HorizontalChoiceSelector from '../../../../components/common/horizontal-choice-selector/HorizontalChoiceSelector'
import IconLockOpen from '../../../../components/common/icon/IconLockOpen'
import IconLock from '../../../../components/common/icon/IconLock'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { linkBuilder } from 'shared/linkBuilder'
import dayjs from 'shared/dayjs'
import { useCreateEventMutation } from 'front-end/src/pages/manage-events/components/create-event-modal/CreateEventModal.codegen'
import { getDefaultEventTime } from 'front-end/src/pages/manage-events/event-edit/tabs/event-detail/eventDetailUtils'

const CreateEventModal: FC<{
  show: boolean
  onHide: () => void
}> = ({ onHide, show }) => {
  const [createEvent] = useCreateEventMutation({
    onCompleted: (data) => {
      onHide()
      setTitle('')
      navigateTo(linkBuilder.manageEvent(data.admin.createEvent.id))
    }
  })
  const [title, setTitle] = useState('')
  const [isOpen, setIsOpen] = useState(true)
  const { defaultStartTime, defaultEndTime } = getDefaultEventTime()

  const firstInputRef = useRef<HTMLInputElement>(null)
  const options = [
    {
      value: true,
      header: (
        <>
          <IconLockOpen />
          <h3>
            <Trans>Public</Trans>
          </h3>
        </>
      ),
      description: (
        <Box className="description">
          <span>
            <Trans>
              Anyone in your organisation will be allowed to RSVP for this event
            </Trans>
          </span>
        </Box>
      )
    },
    {
      value: false,
      header: (
        <>
          <IconLock />
          <h3>
            <Trans>Private</Trans>
          </h3>
        </>
      ),
      description: (
        <Box className="description">
          <span>
            <Trans>Only invited members will be allowed to view or RSVP</Trans>
          </span>
        </Box>
      )
    }
  ]

  return (
    <Modal
      newDesign
      className="CreateEventModal"
      show={show}
      onHide={onHide}
      onShown={() => {
        firstInputRef?.current?.focus()
      }}
      controls={[
        <ButtonClean onClick={onHide} key={'clear'}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          key={'create'}
          onClick={async () => {
            await createEvent({
              variables: {
                title,
                isOpen,
                timezone: dayjs.tz.guess(),
                startTime: defaultStartTime,
                endTime: defaultEndTime
              }
            })
          }}
          disabled={!title.length}
        >
          <Trans>Create</Trans>
        </ButtonPrimary>
      ]}
    >
      <Heading size="md" p="40px 0 8px">
        <Trans>Create a new event</Trans>
      </Heading>
      <FormControl mb="16px">
        <FormLabel>
          <Trans>Name</Trans>
        </FormLabel>
        <Input
          name="title"
          id="title"
          className="form-textInput"
          type="text"
          mt="4px"
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t`Name your new event`}
          ref={firstInputRef}
        />
      </FormControl>
      <FormControl
        className="form-row"
        css={css`
          .HorizontalChoiceSelector {
            margin-top: 8px;
          }
        `}
      >
        <FormLabel>
          <Trans>Visibility</Trans>
        </FormLabel>

        <HorizontalChoiceSelector
          name="type"
          onChange={(val) => {
            setIsOpen(val)
          }}
          options={options}
          value={isOpen}
        />
      </FormControl>
    </Modal>
  )
}

export { CreateEventModal }
